import type { MerchantAppReturn } from "@redotech/redo-model/return";
import { eq } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

export interface UseCustomerReturnsResult {
  returns: MerchantAppReturn[];
  pending: boolean;
  error: Error | undefined;
}

export function useCustomerReturns(
  customerEmail?: string | null,
): UseCustomerReturnsResult {
  const { returns: returnsCollection } = useGlobalConversationCollections();

  const returnsQuery = useLiveQuery(
    (q) =>
      q
        .from({ return: returnsCollection })
        .where(({ return: returnItem }) =>
          eq(returnItem.shipping_address?.email, customerEmail ?? ""),
        ),
    [customerEmail],
  );

  // If no email provided, return empty results without querying
  if (!customerEmail) {
    return { returns: [], pending: false, error: undefined };
  }

  return {
    returns: returnsQuery.data ?? [],
    pending: returnsQuery.isLoading,
    error: returnsQuery.isError
      ? new Error("Failed to get customer returns from collection")
      : undefined,
  };
}
